import {
  ColorClass,
  Icon,
  IconIdentifier,
  Placement,
  Spinner,
  Tooltip,
} from '@aus-platform/design-system';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type CopyableTextProps = {
  header?: string | ReactNode;
  value: string;
  copyText?: string;
  isLoading?: boolean;
  className?: string;
  showCopyIcon?: boolean;
  iconSize?: number;
};

export const CopyableText: React.FC<CopyableTextProps> = ({
  header,
  value,
  isLoading,
  copyText = value,
  className,
  showCopyIcon = true,
  iconSize = 14,
}) => {
  // useStates.
  const [isCopied, setIsCopied] = useState(false);

  // useRef.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // useEffects.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // useCallbacks.
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(String(copyText));
    setIsCopied(true);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setIsCopied(false);
      timerRef.current = null;
    }, 1000);
  }, [copyText]);

  const containerClassName = classNames('copyable-text', className);

  return (
    <div className={containerClassName}>
      {header && <div className="copyable-text__label">{header}</div>}
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="copyable-text__content">
          {value} &nbsp;
          {showCopyIcon && (
            <Tooltip
              hoverText={isCopied ? 'Copied' : 'Copy'}
              className={isCopied ? 'copyable-text__copied' : ''}
              placement={Placement.Top}
              show={isCopied || undefined}
            >
              <span>
                <Icon
                  identifier={IconIdentifier.Copy}
                  size={iconSize}
                  colorClass={ColorClass.AccentPrimary}
                  onClick={handleCopy}
                />
              </span>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};
